import { auth } from '../config/firebase';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

async function authHeaders(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) return {};
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface MyListing {
  listingId: string;
  price: number;
  quantity: number | null;
  splitType: string | null;
}

export interface SplitRow {
  splitType: string;
  myMinPrice: number | null;
  competitorMinPrice: number | null;
  // Original source-currency price (Hello = USD), for hover tooltips
  myMinPriceUsd?: number | null;
  competitorMinPriceUsd?: number | null;
}

export interface CategoryRow {
  categoryName: string;
  normalisedCategoryName: string;
  myListings: MyListing[];
  splitBreakdown: SplitRow[];
  myMinPrice: number | null;
  competitorMinPrice: number | null;
}

export interface NormalisedEvent {
  // Identity of the FIXTURE, not the name: see buildEventKeys. Used as the cross-platform join key.
  eventKey: string;
  eventName: string;
  // The plain normalised name, WITHOUT the date. Kept separate from eventKey because this is what
  // gets persisted on listing_minimums server-side — a composite key must never leak into that.
  normalisedEventName: string;
  eventDate: string | null;
  venue: string | null;
  categories: CategoryRow[];
  // Section-specific listings (Hello only) — same shape as categories, shown in a separate area.
  sections: CategoryRow[];
}

export interface ListingsData {
  live: Map<string, NormalisedEvent>;
  ftn: Map<string, NormalisedEvent>;
  hello: Map<string, NormalisedEvent>;
  stubhub: Map<string, NormalisedEvent>;
  vivid: Map<string, NormalisedEvent>;
}

// Shape returned by the server (DB document, already normalised by the bot)
interface DbEventListing {
  eventName: string;
  normalisedEventName: string;
  eventDate?: string | null;
  venue?: string | null;
  categories: CategoryRow[];
  sections?: CategoryRow[];
}

// ── Event identity ────────────────────────────────────────────────────────────

// A fixture is identified by name AND when it is played. Keying on the name alone silently
// collapsed distinct fixtures: `Map.set` overwrote, so of two same-named games only ONE ever
// reached the screen and the other vanished with its listings. That hit both directions —
// two fixtures on ONE platform (Liverpool vs Tottenham on 7 Sep and again on 19 Dec), and
// worse, DIFFERENT fixtures across platforms merged into a single row, so the my-price vs
// competitor-price comparison was reading two unrelated matches side by side.
//
// A rescheduled game is NOT a second fixture, though, and must stay one row. Two same-named
// events count as the same fixture when they are close in time AND at the same ground — the
// same date-move rule the market-data gatherer uses. A different venue means a different
// competition (Chelsea vs Man City: Stamford Bridge in the league, Wembley in the cup), so it
// splits even when the dates are near.
const DATE_MOVE_WINDOW_DAYS = 7;

const dayOf = (d?: string | null) => (d ? new Date(d).toISOString().slice(0, 10) : '');
// Ground names differ by filler between platforms — Vivid "Selhurst Park Stadium", Live "Selhurst
// Park" — and that split 17 Palace fixtures into two rows each. Drop the generic words, and treat
// one name that merely extends the other as the same ground.
const venueOf = (v?: string | null) =>
  (v || '').toLowerCase().replace(/\b(stadium|stadion|arena|ground|the)\b/g, '').replace(/[^a-z0-9]/g, '');
const sameGround = (a: string, b: string) => !a || !b || a === b || a.startsWith(b) || b.startsWith(a);

interface PlatformDoc {
  platform: string;
  doc: DbEventListing;
}

// Group every platform's docs into fixtures and hand each one a shared key. Done across ALL
// platforms at once, never per-platform: the key is the cross-platform join, so each side has to
// arrive at the same answer from its own copy of the fixture.
function buildEventKeys(all: PlatformDoc[]): Map<DbEventListing, string> {
  const byName = new Map<string, PlatformDoc[]>();
  for (const entry of all) {
    const name = entry.doc.normalisedEventName;
    if (!byName.has(name)) byName.set(name, []);
    byName.get(name)!.push(entry);
  }

  const keys = new Map<DbEventListing, string>();
  for (const [name, entries] of byName) {
    // An undated event can't be clustered by date; keep them together under one key, which is
    // what keying by name alone used to do for every event.
    for (const e of entries.filter((x) => !x.doc.eventDate)) keys.set(e.doc, `${name}|undated`);

    const dated = entries
      .filter((x) => x.doc.eventDate)
      .sort((a, b) => new Date(a.doc.eventDate!).getTime() - new Date(b.doc.eventDate!).getTime());

    let cluster: PlatformDoc[] = [];
    const flush = () => {
      if (!cluster.length) return;
      const key = `${name}|${dayOf(cluster[0].doc.eventDate)}`;
      for (const e of cluster) keys.set(e.doc, key);
      cluster = [];
    };
    for (const entry of dated) {
      if (!cluster.length) {
        cluster.push(entry);
        continue;
      }
      const gapDays =
        (new Date(entry.doc.eventDate!).getTime() - new Date(cluster[0].doc.eventDate!).getTime()) / 864e5;
      // A missing venue on either side is not evidence of a different ground — don't split on it.
      const sameVenue = sameGround(venueOf(cluster[0].doc.venue), venueOf(entry.doc.venue));
      if (gapDays <= DATE_MOVE_WINDOW_DAYS && sameVenue) cluster.push(entry);
      else {
        flush();
        cluster.push(entry);
      }
    }
    flush();
  }
  return keys;
}

function toNormalised(doc: DbEventListing, eventKey: string): NormalisedEvent {
  return {
    eventKey,
    eventName: doc.eventName,
    normalisedEventName: doc.normalisedEventName,
    eventDate: doc.eventDate ?? null,
    venue: doc.venue ?? null,
    categories: doc.categories ?? [],
    sections: doc.sections ?? [],
  };
}

// ── Platform adapters ─────────────────────────────────────────────────────────

interface PlatformAdapter {
  platform: string;
  fetch(): Promise<DbEventListing[]>;
}

const LiveAdapter: PlatformAdapter = {
  platform: 'live',
  async fetch() {
    const headers = await authHeaders();
    const { data } = await axios.get<DbEventListing[]>(`${API_URL}/listings/live`, { headers });
    return data;
  },
};

const FtnAdapter: PlatformAdapter = {
  platform: 'ftn',
  async fetch() {
    const headers = await authHeaders();
    const { data } = await axios.get<DbEventListing[]>(`${API_URL}/listings/ftn`, { headers });
    return data;
  },
};

const HelloAdapter: PlatformAdapter = {
  platform: 'hello',
  async fetch() {
    const headers = await authHeaders();
    const { data } = await axios.get<DbEventListing[]>(`${API_URL}/listings/hello`, { headers });
    return data;
  },
};

const StubhubAdapter: PlatformAdapter = {
  platform: 'stubhub',
  async fetch() {
    const headers = await authHeaders();
    const { data } = await axios.get<DbEventListing[]>(`${API_URL}/listings/stubhub`, { headers });
    return data;
  },
};

const VividAdapter: PlatformAdapter = {
  platform: 'vivid',
  async fetch() {
    const headers = await authHeaders();
    const { data } = await axios.get<DbEventListing[]>(`${API_URL}/listings/vivid`, { headers });
    return data;
  },
};

// ── Registry — add new platforms here ────────────────────────────────────────

const PLATFORM_ADAPTERS: PlatformAdapter[] = [LiveAdapter, FtnAdapter, HelloAdapter, StubhubAdapter, VividAdapter];

// ── Public API ────────────────────────────────────────────────────────────────

export async function getAllListings(): Promise<ListingsData> {
  const results = await Promise.all(PLATFORM_ADAPTERS.map((a) => a.fetch()));

  const all: PlatformDoc[] = [];
  results.forEach((docs, i) => {
    const platform = PLATFORM_ADAPTERS[i].platform;
    for (const doc of docs) if (doc.eventName) all.push({ platform, doc });
  });
  const keys = buildEventKeys(all);

  const maps: Record<string, Map<string, NormalisedEvent>> = {
    live: new Map(), ftn: new Map(), hello: new Map(), stubhub: new Map(), vivid: new Map(),
  };
  for (const { platform, doc } of all) {
    const key = keys.get(doc)!;
    const normalised = toNormalised(doc, key);
    const existing = maps[platform].get(key);
    if (!existing) {
      maps[platform].set(key, normalised);
      continue;
    }
    // Two docs from ONE platform in the same fixture — a stale copy left behind after a date move,
    // or two platform event ids for one game. Last-in-wins silently hid the live one: Arsenal vs
    // Dortmund showed only its paused Box/Diamond categories because a stale doc happened to be
    // processed second. Merge instead, so no category is lost whichever order they arrive in.
    maps[platform].set(key, {
      ...(normalised.categories.length >= existing.categories.length ? normalised : existing),
      categories: [...existing.categories, ...normalised.categories],
      sections: [...existing.sections, ...normalised.sections],
      eventKey: key,
    });
  }
  return { live: maps.live, ftn: maps.ftn, hello: maps.hello, stubhub: maps.stubhub, vivid: maps.vivid };
}

// ── Minimum price floors ───────────────────────────────────────────────────────

export interface MinimumDoc {
  platform: string;
  listingId: string;
  minimumPrice: number;
}

export interface TargetListing {
  platform: 'live' | 'ftn' | 'hello' | 'stubhub' | 'vivid';
  listingId: string;
  eventId?: string;
  normalisedEventName?: string;
  categoryName?: string;
  splitType?: string;
}

// Minimums come back as a compact keyed map { `${platform}:${listingId}`: minimumPrice } (the server
// stores/serves them this way so it doesn't hold/rebuild a 52k-object array — see listings.service).
export async function getMinimums(): Promise<Record<string, number>> {
  const headers = await authHeaders();
  const { data } = await axios.get<Record<string, number>>(`${API_URL}/listings/minimums`, { headers });
  return data;
}

// The server skips any LOCKED listing in the set and reports how many it left alone.
export async function setMinimum(
  minimumPrice: number | null,
  listings: TargetListing[],
): Promise<{ updated: number; cleared: number; lockedSkipped: number }> {
  const headers = await authHeaders();
  const { data } = await axios.post(`${API_URL}/listings/minimums`, { minimumPrice, listings }, { headers });
  return { updated: data?.updated ?? 0, cleared: data?.cleared ?? 0, lockedSkipped: data?.lockedSkipped ?? 0 };
}

// ── Locked minimums ───────────────────────────────────────────────────────────
// A locked minimum is frozen: every dashboard write skips it (enforced server-side) and the bots
// leave the listing's price alone. Keys are `${platform}:${listingId}`, like the minimums map.
export async function getLockedMinimums(): Promise<Set<string>> {
  const headers = await authHeaders();
  const { data } = await axios.get<string[]>(`${API_URL}/listings/minimums/locked`, { headers });
  return new Set(Array.isArray(data) ? data : []);
}

export async function setMinimumsLocked(
  listings: TargetListing[],
  locked: boolean,
): Promise<{ updated: number }> {
  const headers = await authHeaders();
  const { data } = await axios.post(`${API_URL}/listings/minimums/lock`, { listings, locked }, { headers });
  return { updated: data?.updated ?? 0 };
}

// Take Hello listings off sale (status inactive) or put them back (active), via the server's bulk
// status endpoint. Used for the block-sections "take down" button — pass ONLY section listing ids so
// the category-generic listings are untouched. Server resolves ids → external_ids and PATCHes Hello
// in bulk, so this is slow-ish (~10-20s) but a single click for hundreds of listings.
export async function setHelloListingsStatus(
  listingIds: string[],
  status: 'active' | 'inactive',
): Promise<{ requested: number; matched: number; changed: number; status: string }> {
  const headers = await authHeaders();
  const { data } = await axios.post(
    `${API_URL}/listings/hello/status`,
    { listingIds, status },
    { headers, timeout: 120000 },
  );
  return data;
}

// One listing plus the minimum to set on it (the bulk endpoint takes a heterogeneous list).
export type ListingMinimumItem = TargetListing & { minimumPrice: number };

// Bulk-set per-listing minimums for a big "take down" in ONE click. Sends the items in sequential
// chunks (capped by both count and serialized bytes so each request stays under the server's ~100kb
// body limit) — never in parallel, so the server isn't flooded. Reports honest partial success:
// keeps going if a chunk fails and returns how many landed vs how many chunks errored.
export async function setMinimumsBulk(
  items: ListingMinimumItem[],
): Promise<{ updated: number; attempted: number; failedChunks: number; lockedSkipped: number }> {
  const headers = await authHeaders(); // fetched once, reused for every chunk
  const MAX_ITEMS = 300;
  const MAX_BYTES = 80_000;

  // Build chunks bounded by count AND payload size (event/category names vary a lot in length).
  const chunks: ListingMinimumItem[][] = [];
  let cur: ListingMinimumItem[] = [];
  let curBytes = 2; // for the enclosing []
  for (const item of items) {
    const size = JSON.stringify(item).length + 1; // +1 for the comma
    if (cur.length > 0 && (cur.length >= MAX_ITEMS || curBytes + size > MAX_BYTES)) {
      chunks.push(cur);
      cur = [];
      curBytes = 2;
    }
    cur.push(item);
    curBytes += size;
  }
  if (cur.length) chunks.push(cur);

  let updated = 0;
  let attempted = 0;
  let failedChunks = 0;
  let lockedSkipped = 0;
  for (const chunk of chunks) {
    attempted += chunk.length;
    try {
      const { data } = await axios.post<{ updated: number; lockedSkipped?: number }>(
        `${API_URL}/listings/minimums/bulk`,
        { items: chunk },
        { headers },
      );
      updated += data?.updated ?? 0;
      lockedSkipped += data?.lockedSkipped ?? 0;
    } catch {
      failedChunks += 1;
    }
  }
  return { updated, attempted, failedChunks, lockedSkipped };
}
